'use client';

import { Table } from '@ria-client/tw-shadcn/components/table';
import type { CSSProperties, ComponentProps, ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';

export interface SummaryTableContainerProps {
  children: ReactNode;
  tableProps?: ComponentProps<'table'>;
  tableContainerProps?: ComponentProps<'div'>;
}

export function SummaryTableContainer({
  children,
  tableProps,
  tableContainerProps,
}: SummaryTableContainerProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  const [fade, setFade] = useState('none');
  const { className: tableClassName, ...restTableProps } = tableProps ?? {};
  const { className: containerClassName, ...restContainerProps } = tableContainerProps ?? {};

  useEffect(() => {
    const contentElement = contentRef.current;
    if (!contentElement) return;

    const updateFade = () => {
      const { clientWidth, scrollLeft, scrollWidth } = contentElement;
      const left = scrollLeft > 0;
      const right = scrollLeft < scrollWidth - clientWidth;
      setFade(
        left && right
          ? 'linear-gradient(90deg, transparent 0%, rgba(0, 0, 0, 0.7) 5%, rgba(0, 0, 0, 1) 10%, rgba(0, 0, 0, 0.7) 95%, transparent 100%)'
          : right
            ? 'linear-gradient(to left, transparent, black 5%)'
            : left
              ? 'linear-gradient(to right, transparent, black 5%)'
              : 'none',
      );
    };

    updateFade();
    contentElement.addEventListener('scroll', updateFade);
    window.addEventListener('resize', updateFade);
    return () => {
      contentElement.removeEventListener('scroll', updateFade);
      window.removeEventListener('resize', updateFade);
    };
  }, []);

  return (
    <div className="relative max-w-[1280px]">
      <div
        {...restContainerProps}
        className={`w-full overflow-x-auto overflow-y-hidden [mask-image:var(--fade)] [-webkit-mask-image:var(--fade)] md:overflow-x-hidden md:[mask-image:none] md:[-webkit-mask-image:none] ${containerClassName ?? ''}`}
        ref={contentRef}
        style={{ '--fade': fade, ...tableContainerProps?.style } as CSSProperties}
      >
        <Table
          {...restTableProps}
          className={`typography-text border-0 md:min-w-[775px] ${tableClassName ?? ''}`}
        >
          {children}
        </Table>
      </div>
    </div>
  );
}
